import { Button, Checkbox, Input, InputNumber, message, Modal, Select, Upload } from 'antd';
import axiosInstance from '../../../../axios/axiosInstance';
import { theoryStatusList, theoryTypeList } from '../../../../utils/enumMapper';
import React, { useEffect, useState } from 'react';
import { useMasterGradesSelector, useMasterSubjectsSelector } from '../../../../redux/selector';
import { ModePage } from 'common/enum';
import processError from '../../../../utils/processError';
import { Content, FormatContentEnum, Theory } from 'model/response';
import { CloseOutlined, UploadOutlined } from '@ant-design/icons';
import toast, { NotifyType } from 'utils/toast';
import { uploadFile } from 'service/fileUploadService';

interface Props {
  isOpen: boolean;
  onCloseModal: () => void;
  modePage: ModePage;
  onSave: () => void;
  theoryId: string;
}

export default function AddNewTheory({ isOpen, onCloseModal, modePage, onSave, theoryId }: Props) {
  const [newTheory, setTheory] = useState<Theory | null>(null);
  const gradeList = useMasterGradesSelector();
  const subjectList = useMasterSubjectsSelector();
  const [loadingContent, setLoadingContent] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);

  const onInputField = (e: any, fieldName: string) => {
    const newTheoryTemp = { ...newTheory } as any;
    newTheoryTemp[fieldName] = e.target.value;

    setTheory(newTheoryTemp);
  };

  const onCheckboxField = (e: any, fieldName: string) => {
    const newTheoryTemp = { ...newTheory } as any;
    newTheoryTemp[fieldName] = e.target.checked;

    setTheory(newTheoryTemp);
  };

  const onChangeSelect = (e: any, fieldName: string) => {
    const newTheoryTemp = { ...newTheory } as any;
    if(fieldName === 'subject') {
      newTheoryTemp[fieldName] = subjectList.find((item: any) => item.id === e); 
    } else if(fieldName === 'grade') {
      newTheoryTemp[fieldName] = gradeList.find((item: any) => item.id === e); 
    } else {
      newTheoryTemp[fieldName] = e;
    }
    setTheory(newTheoryTemp);
  };

  const loadNewTheory = async () => {
    setTheory({
      id: '',
      webViewActive: true,
      webViewOrder: 1,
      status: 'ACTIVE',
      code: '',
      name: '',
      audioPath: '',
      contents: [],
      url: '',
      type: 'BASIC',
    } as any);
  };

  const loadEditTheory = async () => {
    try {
      const res = await axiosInstance({
        url: `/theories/${theoryId}`,
        method: 'get',
      });

      if (res.status === 200) {
        setTheory(res.data);
      } else {
        message.error('Có lỗi xảy ra.');
      }
    } catch (err) {
      processError(err);
    }
  };

  const updateContent = (index: number, key: keyof Content, value: any) => {
    if (newTheory) {
      const updatedContents = newTheory.contents ? [...newTheory.contents] : [];

      updatedContents[index] = { ...updatedContents[index], [key]: value };

      setTheory({ ...newTheory, contents: updatedContents });
    }
  };

  const onOk = async () => {
    if (!newTheory?.name) {
      message.error('Vui lòng nhập tên lý thuyết');
      return;
    }
    if (!newTheory?.webViewOrder && newTheory?.webViewOrder !== 0) {
      message.error('Vui lòng nhập thứ tự hiển thị');
      return;
    }
    if (!newTheory?.subject) {
      message.error('Vui lòng chọn môn học');
      return;
    }
    try {
      const res = await axiosInstance({
        url: '/theories',
        method: modePage === ModePage.ADD ? 'post' : 'put',
        data: newTheory,
      });

      if (res.status === 200) {
        message.success(modePage === ModePage.ADD ? 'Thêm thành công.' : 'Sửa thành công.');
        onSave();
      } else {
        message.error('Có lỗi xảy ra.');
      }
    } catch (err) {
      processError(err);
    }
  };

  const handleFileChange = (info: any, index: number, format: string) => {
    const file = info.file;
    if (file) {
      let validTypes: string[] = [];
      let fileType = '';

      if (format === 'AUDIO') {
        validTypes = ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/aac', 'audio/ogg'];
        fileType = 'url';
        setLoadingContent(true);
      } else if (format === 'VIDEO') {
        validTypes = ['video/mp4', 'video/ogg', 'video/webm', 'video/avi', 'video/mkv'];
        fileType = 'url';
        setLoadingContent(true);
      } else if (format === 'TEXT') {
        validTypes = [
          'application/msword',
          'application/pdf',
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        ];
        fileType = 'url';
        setLoadingContent(true);
      } else if (format === 'AUDIO_GENERATED') {
        validTypes = ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/aac', 'audio/ogg'];
        fileType = 'audioPath';
        setLoadingAudio(true);
      }

      // Kiểm tra loại tệp có hợp lệ không
      if (!validTypes.includes(file.type)) {
        toast(NotifyType.WARNING, `Vui lòng tải đúng định dạng ${format.toLowerCase()}.`);
        setLoadingAudio(false);
        setLoadingContent(false);
        return;
      }

      const formData = new FormData();
      formData.append('file', file);

      uploadFile(formData)
        .then((data) => {
          if (data?.status === 1) {
            setTheory((prevTheory) => {
              if (!prevTheory) return prevTheory;

              // Cập nhật đúng phần tử content trong contents dựa trên index
              const updatedContents = [...(prevTheory.contents || [])];
              updatedContents[index] = {
                ...updatedContents[index],
                [fileType]: data.url, // Cập nhật đường dẫn tệp tương ứng
              };

              return {
                ...prevTheory,
                contents: updatedContents,
              };
            });
            setLoadingAudio(false);
            setLoadingContent(false);
          } else {
            toast(NotifyType.ERROR, 'Upload file thất bại, vui lòng thử lại.');
            setLoadingAudio(false);
            setLoadingContent(false);
          }
        })
        .catch((error) => {
          setLoadingAudio(false);
          setLoadingContent(false);
          console.log(error);
          toast(NotifyType.ERROR, 'Upload file lỗi!');
        });
    } else {
      setLoadingAudio(false);
      setLoadingContent(false);
      toast(NotifyType.ERROR, 'Upload file lỗi!');
    }
  };

  const addNewContent = () => {
    if (newTheory) {
      const newContent = {
        parentId: newTheory?.id,
        status: 1,
      } as any;
      const updatedContents = newTheory.contents ? [...newTheory.contents, newContent] : [newContent];

      setTheory({ ...newTheory, contents: updatedContents });
    }
  };

  const removeContent = (index: number) => {
    if (newTheory) {
      const updatedContents = [...(newTheory.contents || [])];
      updatedContents.splice(index, 1); // Xóa phần tử tại vị trí index
      setTheory({ ...newTheory, contents: updatedContents });
    }
  };

  useEffect(() => {
    if (isOpen) {
      modePage === ModePage.ADD ? loadNewTheory() : loadEditTheory();
    }
  }, [isOpen]);

  return (
    <Modal open={isOpen} onOk={onOk} onCancel={onCloseModal}>
      <div className="mt-8">
        <div className="flex items-center gap-2">
          <div className="w-[210px]">
            Tên lý thuyết <span className={'text-FF1D30'}>*</span>:
          </div>
          <Input maxLength={255} name="name" onChange={(e) => onInputField(e, 'name')} value={newTheory?.name} />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-[210px]">Mã lý thuyết:</div>
          <Input maxLength={255} name="code" onChange={(e) => onInputField(e, 'code')} value={newTheory?.code} />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-[140px]">Loại lý thuyết:</div>
          <Select
            onChange={(e) => onChangeSelect(e, 'type')}
            className="w-full"
            placeholder="Loại lý thuyết"
            style={{ flex: 1 }}
            value={newTheory?.type}
            options={theoryTypeList.map((item: any) => ({
              value: item.value,
              label: item.label,
            }))}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-[140px]">Trạng thái:</div>
          <Select
            onChange={(e) => onChangeSelect(e, 'status')}
            className="w-full"
            placeholder="Trạng thái"
            style={{ flex: 1 }}
            value={newTheory?.status}
            options={theoryStatusList}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-[210px]">Thumbnails:</div>
          <Input
            maxLength={255}
            name="thumbnails"
            onChange={(e) => onInputField(e, 'thumbnails')}
            value={newTheory?.thumbnails}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-[210px]">URL:</div>
          <Input maxLength={255} name="url" onChange={(e) => onInputField(e, 'url')} value={newTheory?.url} />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-[210px]">Mô tả:</div>
          <Input
            maxLength={500}
            name="description"
            onChange={(e) => onInputField(e, 'description')}
            value={newTheory?.description}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-[140px]">
            Môn học <span className={'text-FF1D30'}>*</span>:
          </div>
          <Select
            onChange={(e) => onChangeSelect(e, 'subject')}
            className="w-full"
            style={{ flex: 1 }}
            value={newTheory && newTheory.subject ? newTheory.subject.id : ''}
            options={subjectList.map((item: any) => ({
              value: item.id,
              label: item.name,
            }))}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-[140px]">Lớp:</div>
          <Select
            onChange={(e) => onChangeSelect(e, 'grade')}
            className="w-full"
            style={{ flex: 1 }}
            value={newTheory && newTheory.grade ? newTheory.grade.id : ''}
            options={gradeList.map((item: any) => ({
              value: item.id,
              label: item.name,
            }))}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <label className="w-[140px]">Hiển thị trên web:</label> <br />
          <Checkbox checked={newTheory?.webViewActive} onChange={(e) => onCheckboxField(e, 'webViewActive')} />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <label className="w-[140px]">
            Độ ưu tiên hiển thị <span className={'text-FF1D30'}>*</span>:
          </label>{' '}
          <br />
          <InputNumber
            placeholder="Nhập độ ưu tiên hiển thị..."
            value={newTheory?.webViewOrder}
            onChange={(e) =>
              newTheory &&
              setTheory({
                ...newTheory,
                webViewOrder: e || 1,
              })
            }
            required
          />
        </div>
        <div>
          <Button onClick={addNewContent} className="mt-4" type="primary">
            Thêm nội dung
          </Button>
        </div>
        {newTheory?.contents &&
          newTheory?.contents.map((content, index) => (
            <div key={index} className="relative mt-4 p-5 border border-gray-300 rounded-md">
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={() => removeContent(index)}
                className="absolute top-[2px] right-[2px]"
              />

              <div className="flex items-center gap-2 mt-2">
                <div className="min-w-[140px]">Format:</div>
                <Select
                  value={content.format}
                  onChange={(value) => updateContent(index, 'format', value)}
                  className="w-full"
                  options={[
                    { value: 'TEXT', label: 'Text' },
                    { value: 'VIDEO', label: 'Video' },
                    { value: 'AUDIO', label: 'Audio' },
                  ]}
                />
              </div>
              {(content.format === 'TEXT' || content.format === 'AUDIO' || content.format === 'VIDEO') && (
                <div className="flex items-center gap-2 mt-2 w-full">
                  <div className="min-w-[140px]">
                    {content.format === 'VIDEO'
                      ? 'Tệp video:'
                      : content.format === FormatContentEnum.TEXT
                        ? 'Tài liệu'
                        : 'Tệp âm thanh:'}
                  </div>
                  <div className="flex items-center w-full">
                    <Upload
                      name={content.format === 'VIDEO' ? 'video' : 'audio'}
                      accept={
                        content.format === 'VIDEO'
                          ? 'video/*'
                          : content.format === 'TEXT'
                            ? '.doc,.docx,.pdf,.ppt,.pptx'
                            : 'audio/*'
                      }
                      className="w-auto"
                      beforeUpload={() => false}
                      onChange={(info) => handleFileChange(info, index, content?.format)}
                      maxCount={1}
                      showUploadList={false}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        className="rounded-none rounded-l-md bg-blue-500 text-white hover:bg-blue-600 border border-gray-300 border-r-0"
                        loading={loadingContent}
                      >
                        {content.format === 'VIDEO'
                          ? 'Tải video'
                          : content.format === 'TEXT'
                            ? 'Tải tài liệu'
                            : 'Tải audio'}
                      </Button>
                    </Upload>
                    <Input
                      value={content?.url}
                      onChange={(e) => updateContent(index, 'url', e.target.value)}
                      className="rounded-l-none flex-1"
                    />
                  </div>
                </div>
              )}
              {/* Chỉ hiển thị khi content.format là 'TEXT' */}
              {content.format === 'TEXT' && (
                <div className="flex items-center gap-2 mt-2 w-full">
                  <div className="min-w-[140px]">Tệp audio:</div>
                  <div className="flex items-center w-full">
                    <Upload
                      name="url"
                      accept="audio/*"
                      className="w-auto"
                      beforeUpload={() => false}
                      onChange={(info) => handleFileChange(info, index, 'AUDIO_GENERATED')}
                      maxCount={1}
                      showUploadList={false}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        loading={loadingAudio}
                        className="rounded-none rounded-l-md bg-blue-500 text-white hover:bg-blue-600 border border-gray-300 border-r-0"
                      >
                        Tải audio
                      </Button>
                    </Upload>
                    <Input
                      value={content.audioPath}
                      readOnly
                      className="rounded-l-none flex-1"
                      onChange={(e) => updateContent(index, 'url', e.target.value)}
                    />
                  </div>
                </div>
              )}

              {content?.format !== FormatContentEnum.TEXT && (
                <div className="flex items-center gap-2 mt-2">
                  <div className="min-w-[140px]">Transcript Text:</div>
                  <Input.TextArea
                    value={content.transcriptText}
                    onChange={(e) => updateContent(index, 'transcriptText', e.target.value)}
                    className="w-full"
                    rows={4} // Số dòng hiển thị mặc định
                  />
                </div>
              )}

              {/* Code */}
              <div className="flex items-center gap-2 mt-2">
                <div className="min-w-[140px]">Code:</div>
                <Input
                  value={content.code}
                  onChange={(e) => updateContent(index, 'code', e.target.value)}
                  className="w-full"
                />
              </div>

              {/* Content Order */}
              <div className="flex items-center gap-2 mt-2">
                <div className="min-w-[140px]">Thứ tự:</div>
                <Input
                  type="number"
                  value={content.contentOrder}
                  onChange={(e) => updateContent(index, 'contentOrder', e.target.value)}
                  className="w-full"
                />
              </div>

              {/* Status */}
              <div className="flex items-center gap-2 mt-2">
                <div className="min-w-[140px]">Trạng thái:</div>
                <Select
                  value={content.status}
                  onChange={(value) => updateContent(index, 'status', value)}
                  className="w-full"
                  options={[
                    { value: 1, label: 'Hiển thị' },
                    { value: 0, label: 'Không hiển thị' },
                  ]}
                />
              </div>
            </div>
          ))}
      </div>
    </Modal>
  );
}
